import type { ColumnStateParams, SortChangedEvent, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-enterprise';
import moment from 'moment';
import { useCallback, useState } from 'react';
import type { BalanceEntryDto, ClientBalanceCategoryDto } from '../api/generated/client';
import type { BalanceSheetTableData } from '../interfaces/balance-sheet-table';

const DATE_SORT_FIELDS = ['fromDate', 'toDate', 'updatedAt'];

const baseParams: Partial<ValueFormatterParams<BalanceSheetTableData>> = {
    column: undefined,
    colDef: undefined,
    api: undefined,
    context: undefined,
    node: undefined,
};

interface SortState {
    field?: string;
    direction?: ColumnStateParams['sort'];
    formatter?: ValueFormatterFunc<BalanceSheetTableData, BalanceEntryDto[keyof BalanceEntryDto]>;
}

export function useBalanceSheetSort() {
    const [sortState, setSortState] = useState<SortState>({});

    const sortEntriesWithinCategories = useCallback(
        (categories: ClientBalanceCategoryDto[]): ClientBalanceCategoryDto[] => {
            if (!sortState.field || !sortState.direction) return categories;

            const factor = sortState.direction === 'asc' ? 1 : -1;

            return categories.map((category) => ({
                ...category,
                entries: [...category.entries].sort((a, b) => {
                    let aVal = a?.[sortState.field as keyof BalanceEntryDto];
                    let bVal = b?.[sortState.field as keyof BalanceEntryDto];

                    if (sortState.field && DATE_SORT_FIELDS.includes(sortState.field)) {
                        const aTime = aVal != null && moment(aVal as string).isValid() ? moment(aVal as string).valueOf() : null;
                        const bTime = bVal != null && moment(bVal as string).isValid() ? moment(bVal as string).valueOf() : null;
                        if (aTime == null && bTime == null) return 0;
                        if (aTime == null) return 1;
                        if (bTime == null) return -1;
                        if (aTime > bTime) return factor;
                        if (aTime < bTime) return -factor;

                        return 0;
                    }

                    if (sortState.formatter && typeof aVal !== 'number') {
                        aVal = sortState.formatter({
                            ...baseParams,
                            value: aVal,
                            data: { ...a, categoryId: category.id, isBankable: category.bankable, isCategoryTitle: false },
                        } as ValueFormatterParams<BalanceSheetTableData>);
                        bVal = sortState.formatter({
                            value: bVal,
                            data: { ...b, categoryId: category.id, isBankable: category.bankable, isCategoryTitle: false },
                        } as ValueFormatterParams<BalanceSheetTableData>);
                    }

                    if (aVal == null && bVal == null) return 0;
                    if (aVal == null) return 1;
                    if (bVal == null) return -1;
                    if (aVal > bVal) return factor;
                    if (aVal < bVal) return -factor;

                    return 0;
                }),
            }));
        },
        [sortState],
    );

    const onSortChanged = useCallback((event: SortChangedEvent) => {
        const sortedCols = event.api.getColumnState().filter((col) => !!col.sort);
        if (sortedCols.length > 0) {
            const colDef = event.api.getColumn(sortedCols[0].colId)?.getColDef();
            setSortState({
                field: sortedCols[0].colId,
                direction: sortedCols[0].sort,
                formatter: typeof colDef?.valueFormatter !== 'string' ? colDef?.valueFormatter : undefined,
            });
        } else {
            setSortState({});
        }
    }, []);

    return { sortState, onSortChanged, sortEntriesWithinCategories };
}
